import React, { useMemo } from 'react';
import { DocumentSummaryCard } from './DocumentSummaryCard';

interface DocumentSummaryCardListProps {
  documents: Array<{ documentId: string; filename: string; pageCount?: number; processingStatus?: string }>;
  metadata: { documentTypes?: Array<{ documentId: string; type: string; confidence: number; summary?: string; citations?: any[] }> } | null;
  chronologyEvents: any[] | undefined;
  viewSummary: { documents: Array<{ documentId: string; viewedPages: number }> } | null;
  onOpenDocument: (documentId: string, filename: string) => void;
  onOpenDocumentAtPage?: (documentId: string, filename: string, pageNumber: number) => void;
}

export const DocumentSummaryCardList: React.FC<DocumentSummaryCardListProps> = ({
  documents,
  metadata,
  chronologyEvents,
  viewSummary,
  onOpenDocument,
  onOpenDocumentAtPage,
}) => {
  const dateRanges = useMemo(() => {
    const map = new Map<string, { earliest: string | null; latest: string | null; topThemes: string[] }>();
    if (!chronologyEvents) return map;
    for (const doc of documents) {
      let earliest: string | null = null;
      let latest: string | null = null;
      const themeCounts = new Map<string, number>();
      for (const event of chronologyEvents) {
        const cites = event.citations?.some((c: any) => c.documentId === doc.documentId);
        if (!cites) continue;
        if (event.eventDate) {
          if (!earliest || event.eventDate < earliest) earliest = event.eventDate;
          if (!latest || event.eventDate > latest) latest = event.eventDate;
        }
        if (event.eventType) {
          themeCounts.set(event.eventType, (themeCounts.get(event.eventType) || 0) + 1);
        }
      }
      const topThemes = Array.from(themeCounts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([t]) => t);
      map.set(doc.documentId, { earliest, latest, topThemes });
    }
    return map;
  }, [documents, chronologyEvents]);

  return (
    <div className="flex-1 overflow-y-auto px-2 py-2 space-y-2">
      {documents.map((doc) => {
        const docType = metadata?.documentTypes?.find((dt) => dt.documentId === doc.documentId);
        const dateRange = dateRanges.get(doc.documentId) ?? { earliest: null, latest: null, topThemes: [] };
        const viewedPages = viewSummary?.documents.find((d) => d.documentId === doc.documentId)?.viewedPages ?? 0;

        return (
          <DocumentSummaryCard
            key={doc.documentId}
            document={doc}
            docType={docType}
            dateRange={dateRange}
            viewedPages={viewedPages}
            chronologyEvents={chronologyEvents}
            onClick={() => onOpenDocument(doc.documentId, doc.filename)}
            onOpenAtPage={onOpenDocumentAtPage
              ? (page) => onOpenDocumentAtPage(doc.documentId, doc.filename, page)
              : undefined
            }
          />
        );
      })}
    </div>
  );
};
